/**
 * DI-3-S74 §3.1 (ex-S72) — known drone-camera images WITHOUT an EXIF
 * position join the flight they were taken in, so they are uploaded with
 * its set and the worker places them from the matched flight log.
 *
 * Who qualifies: a parsed row of a known family (`family !== "unknown"`)
 * without `normalized.lat/lon`. Anything else without a position (a phone
 * photo dropped by mistake) stays in `noPosition` as before, never counted.
 *
 * Which flight (story §3.1.2):
 *   - a provisional flight with POSITIONED rows in the same folder
 *     (`relativePath` dirname);
 *   - whose time span, extended by the 180 s gap rule (`FLIGHT_MERGE_S`,
 *     DI-D39 c4: a gap of 180 s or more starts a new flight), contains the
 *     row's capture time. Joined rows extend the span, so a run of frames a
 *     second apart beyond the last positioned frame stays one flight, exactly
 *     as it would if those frames had a position;
 *   - the same camera serial (`BodySerialNumber`, else XMP
 *     `CameraSerialNumber`) when BOTH sides carry one — a missing serial is
 *     no constraint (Phase 0: the ILX-LR1 v3.00 writes none at all);
 *   - ties → the flight nearer in time (distance to its span; then the
 *     earlier cluster / flight).
 * No such flight (or no capture time) → `unassigned`: not sent, counted in
 * the warning's second sentence.
 *
 * Joined rows are members (cluster + flight `imageIndexes`, so
 * `memberIndexes`, `sendPlan`, the set's window and Add images' folder
 * members all include them) but never move the centroid or the radius
 * (they have no position). No `altRel` → airborne (`isGroundShot`'s rule);
 * a joined row WITH a ground `altRel` goes to the cluster's ground group.
 *
 * Pure: the clusters are returned as new objects, the input is not mutated.
 */
import { cadence } from "./cadence.js";
import { byTimeThenIndex } from "./cluster.js";
import { FLIGHT_MERGE_S, isGroundShot } from "./flights.js";
import type { ClusterGroup, GroupRow, ProvisionalFlight } from "./types.js";

type Parsed = NonNullable<GroupRow["parsed"]>;

/** The folder of a relative path (`""` at the root). */
export function folderOf(relativePath: string): string {
  const k = Math.max(
    relativePath.lastIndexOf("/"),
    relativePath.lastIndexOf("\\"),
  );
  return k < 0 ? "" : relativePath.slice(0, k);
}

function text(v: unknown): string | undefined {
  if (typeof v !== "string") return undefined;
  const s = v.trim();
  return s.length > 0 ? s : undefined;
}

/** `BodySerialNumber`, else XMP `CameraSerialNumber`; undefined when neither. */
export function cameraSerialOf(parsed: Parsed | undefined): string | undefined {
  return (
    text(parsed?.exif?.BodySerialNumber) ??
    text(parsed?.xmp?.CameraSerialNumber)
  );
}

/** A known-camera row without a position (story §3.1.1). */
export function qualifiesForJoin(row: GroupRow): boolean {
  const p = row.parsed;
  if (!p?.family || p.family === "unknown") return false;
  return p.normalized.lat === undefined || p.normalized.lon === undefined;
}

interface Target {
  cluster: number;
  flight: number;
  folders: Set<string>;
  serials: Set<string>;
  lo: number;
  hi: number;
  joined: { index: number; t: number; ground: boolean }[];
}

export interface JoinResult {
  clusters: ClusterGroup[];
  /** Every joined row index, ascending. */
  joined: number[];
  /** Qualifying rows no flight took, ascending. */
  unassigned: number[];
}

export function joinToFlights(
  clusters: readonly ClusterGroup[],
  rows: readonly GroupRow[],
  candidates: readonly number[],
  mergeS: number = FLIGHT_MERGE_S,
): JoinResult {
  if (candidates.length === 0)
    return { clusters: clusters.slice(), joined: [], unassigned: [] };

  const byIndex = new Map(rows.map((r) => [r.index, r]));
  const targets: Target[] = [];
  clusters.forEach((c, ci) => {
    c.flights.forEach((f, fi) => {
      const folders = new Set<string>();
      const serials = new Set<string>();
      for (const i of f.imageIndexes) {
        const r = byIndex.get(i);
        if (!r) continue;
        folders.add(folderOf(r.relativePath));
        const s = cameraSerialOf(r.parsed);
        if (s !== undefined) serials.add(s);
      }
      targets.push({
        cluster: ci,
        flight: fi,
        folders,
        serials,
        lo: f.start,
        hi: f.end,
        joined: [],
      });
    });
  });

  const splitMs = mergeS * 1000;
  const unassigned: number[] = [];
  let pending: {
    index: number;
    t: number;
    folder: string;
    serial?: string;
    ground: boolean;
  }[] = [];
  for (const i of candidates) {
    const r = byIndex.get(i);
    const t = r?.parsed?.normalized.capturedAtMs;
    if (!r || t === undefined) {
      unassigned.push(i);
      continue;
    }
    const serial = cameraSerialOf(r.parsed);
    pending.push({
      index: i,
      t,
      folder: folderOf(r.relativePath),
      ...(serial !== undefined ? { serial } : {}),
      ground: isGroundShot(r.parsed?.normalized.altRel),
    });
  }
  pending.sort(byTimeThenIndex);

  // Passes until nothing joins: a joined airborne row extends its flight's
  // span, which may bring the next frame within 180 s (either direction).
  let changed = true;
  while (changed && pending.length > 0) {
    changed = false;
    const rest: typeof pending = [];
    for (const p of pending) {
      let best: Target | undefined;
      let bestD = Number.POSITIVE_INFINITY;
      for (const tg of targets) {
        if (!tg.folders.has(p.folder)) continue;
        if (
          p.serial !== undefined &&
          tg.serials.size > 0 &&
          !tg.serials.has(p.serial)
        )
          continue;
        if (p.t <= tg.lo - splitMs || p.t >= tg.hi + splitMs) continue;
        const d = p.t < tg.lo ? tg.lo - p.t : p.t > tg.hi ? p.t - tg.hi : 0;
        if (d < bestD) {
          bestD = d;
          best = tg;
        }
      }
      if (!best) {
        rest.push(p);
        continue;
      }
      best.joined.push({ index: p.index, t: p.t, ground: p.ground });
      if (!p.ground) {
        best.lo = Math.min(best.lo, p.t);
        best.hi = Math.max(best.hi, p.t);
      }
      changed = true;
    }
    pending = rest;
  }
  for (const p of pending) unassigned.push(p.index);

  const out = clusters.map((c) => ({ ...c }));
  const joinedAll: number[] = [];
  for (const tg of targets) {
    if (tg.joined.length === 0) continue;
    const c = out[tg.cluster];
    const f = c?.flights[tg.flight];
    if (!c || !f) continue;
    const timeOf = new Map<number, number | undefined>();
    for (const i of c.imageIndexes)
      timeOf.set(i, byIndex.get(i)?.parsed?.normalized.capturedAtMs);
    const airborne = tg.joined.filter((j) => !j.ground);
    const ground = tg.joined.filter((j) => j.ground);
    if (airborne.length > 0) {
      const frames = [
        ...f.imageIndexes.map((i) => ({ index: i, t: timeOf.get(i) ?? 0 })),
        ...airborne.map((j) => ({ index: j.index, t: j.t })),
      ].sort(byTimeThenIndex);
      const next: ProvisionalFlight = {
        ...f,
        start: Math.min(f.start, ...airborne.map((j) => j.t)),
        end: Math.max(f.end, ...airborne.map((j) => j.t)),
        imageIndexes: frames.map((x) => x.index),
        cadence: cadence(frames),
      };
      c.flights = c.flights.map((x, k) => (k === tg.flight ? next : x));
    }
    if (ground.length > 0) {
      c.ground = [
        ...c.ground.map((i) => ({ index: i, t: timeOf.get(i) })),
        ...ground.map((j) => ({ index: j.index, t: j.t })),
      ]
        .sort(byTimeThenIndex)
        .map((x) => x.index);
    }
    for (const j of tg.joined) timeOf.set(j.index, j.t);
    c.imageIndexes = [...c.imageIndexes, ...tg.joined.map((j) => j.index)]
      .map((i) => ({ index: i, t: timeOf.get(i) }))
      .sort(byTimeThenIndex)
      .map((x) => x.index);
    c.joined = [...(c.joined ?? []), ...tg.joined.map((j) => j.index)].sort(
      (a, b) => a - b,
    );
    joinedAll.push(...tg.joined.map((j) => j.index));
  }
  // later flights of the same cluster may have changed — recompute gaps
  for (const c of out) {
    if (!c.joined) continue;
    c.flights = c.flights.map((f, k) => {
      const prev = c.flights[k - 1];
      return k === 0 || !prev
        ? f
        : { ...f, gapBeforeS: (f.start - prev.end) / 1000 };
    });
  }

  return {
    clusters: out,
    joined: joinedAll.sort((a, b) => a - b),
    unassigned: unassigned.sort((a, b) => a - b),
  };
}
